import { useSelector, useStore } from '@/stores/hooks'
import type { Loader } from '@/stores/load/load'
import { getLoadingState, type LoadingState } from '@/stores/loading'
import { mapOrd, rawOrd } from '@lc/libarray'
import { createContext, useContext, useEffect } from 'react'
import { useExecutor } from '../useExecutor'

export type LoadListener = (
	id: string,
	params: { level: number },
	loader: Loader<unknown>,
) => void
export const LoadContext = createContext<LoadListener | null>(null)
LoadContext.displayName = 'LoadContext'

export type UseLoad = {
	id: string
	status: LoadingState['status']
	error?: string
}

export function useLoad<T>(
	load: Loader<T>,
	params: { level: number; disable?: boolean } & T,
): UseLoad {
	const id = load.id(params)
	const store = useStore()
	const executor = useExecutor()
	const loadingState = useSelector(state => getLoadingState(state, id))
	const realStatus = loadingState?.status ?? 'none'

	const loadContext = useContext(LoadContext)
	if (realStatus === 'none' && !params.disable) {
		loadContext?.(id, params, load as Loader<unknown>)
	}

	useEffect(() => {
		if (params.disable) return
		if (realStatus === 'pending' || realStatus === 'loading') return
		void load({ store, executor, ...params })
	}, [
		store,
		executor,
		...Object.entries(params)
			.sort(mapOrd(rawOrd, x => x[0]))
			.flat(),
	])

	const status = realStatus === 'none' ? 'pending' : realStatus
	if (loadingState?.status === 'failed') {
		return { id, status, error: loadingState.error }
	} else {
		return { id, status }
	}
}
